/**
 * Date utility functions for the application
 */

/**
 * Converts a UTC date string to a local datetime string in the format required by HTML datetime-local inputs
 * @param utcDateTimeString - UTC date string to convert
 * @returns Formatted datetime string in YYYY-MM-DDTHH:MM format
 */
export function convertUTCToLocal(utcDateTimeString: string): string {
  const utcDate = new Date(utcDateTimeString);
  return convertToLocalDateTimeString(utcDate);
}

/**
 * Converts a Date object to a local datetime string in the format required by HTML datetime-local inputs
 * @param date - Date object to format
 * @returns Formatted datetime string in YYYY-MM-DDTHH:MM format
 */
export function convertToLocalDateTimeString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
/**
 * Gets the current date and time rounded to the nearest 15 minutes
 * @returns Date object with time rounded to nearest 15 minutes
 */
export function getCurrentTimeRounded(): Date {
  const now = new Date();
  const minutes = now.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  now.setMinutes(roundedMinutes, 0, 0);
  return now;
}
export function toLocalInputFormat(value: string | Date | null | undefined): string {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';

  // If the input is a UTC string (ends with Z or +hh:mm), convert to local time
  const isUTC = typeof value === 'string' && /([Zz]|[+-]\d\d:?\d\d)$/.test(value);
  const localDate = isUTC ? new Date(date.getTime() - date.getTimezoneOffset() * 60000) : date;

  // Build "YYYY-MM-DDTHH:mm" manually — no UTC conversion
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}


export function toUTCISOString(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  return date.toISOString();
}

/**
 * Converts a calendar date (Date object or string) into a clean UTC ISO string
 * representing start-of-day (00:00:00.000Z) or end-of-day (23:59:59.999Z)
 * using the date's local calendar day without timezone roll.
 */
export function toUtcDateIso(d: Date | string | null | undefined, isEnd = false): string | null {
  if (!d) return null;
  if (typeof d === 'string') {
    const match = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [_, y, m, day] = match;
      return isEnd
        ? `${y}-${m}-${day}T23:59:59.999Z`
        : `${y}-${m}-${day}T00:00:00.000Z`;
    }
  }
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return isEnd
    ? `${y}-${m}-${day}T23:59:59.999Z`
    : `${y}-${m}-${day}T00:00:00.000Z`;
}


/**
 * Parses a datetime-local string (like from HTML input) as local time.
 */
export function parseLocalDateTime(localString: string): Date {
  const [datePart, timePart] = localString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hour, minute);
}

/**
 * Converts a Date to `YYYY-MM-DDTHH:mm` (local time)
 */
export function formatToLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}



/**
 * Checks if a date/time is in the past relative to now (UTC-based comparison)
 * @param value - Date object or UTC string
 * @returns true if the date is before now
 */
export function isBeforeNow(value: string | Date): boolean {
  if (!value) return false;

  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return false; // invalid date guard

  const now = new Date();
  return date.getTime() < now.getTime();
}

/**
 * "1 h 30 min" / "2 h" / "45 min" — the house format for a span of time.
 *
 * Product, package and price-tier labels already read this way, so lessons,
 * bookings and rentals share it too rather than keeping a private variant
 * each ("3h 30m", "1h 30min", "1.5h" all used to coexist).
 *
 * A negative span is reported by magnitude; the caller supplies the wording
 * ("… over package").
 */
export function formatDurationMinutes(minutes: number): string {
  const total = Math.abs(Math.round(minutes));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/**
 * Calendar day of a local Date as "YYYY-MM-DD". Unlike
 * `toISOString().slice(0, 10)`, it doesn't shift to UTC: local midnight on
 * 6 Oct in Europe/Madrid is still 2026-10-06, not 2026-10-05.
 */
export function toLocalYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Local midnight of that day as a UTC instant — a day filter's lower bound in the user's own time zone. */
export function localDayStartIso(date: Date): string {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString();
}
